/**
 * Authentication Storage Wrapper
 * 
 * Transparently routes auth_token to cookies while keeping other data in localStorage
 * This provides minimal code changes with maximum security improvement
 */

import { getCookie, setCookie, removeCookie } from './cookieUtils';

// List of keys that should be stored in cookies instead of localStorage
const COOKIE_KEYS = ['auth_token'];

/**
 * Storage wrapper that automatically uses cookies for auth tokens
 * and localStorage for everything else
 */
export const authStorage = {
    /**
     * Get item from storage (cookie for auth_token, localStorage for others)
     * @param {string} key - Storage key
     * @returns {string|null} Value or null if not found
     */
    getItem(key) {
        if (COOKIE_KEYS.includes(key)) {
            return getCookie(key);
        }
        return localStorage.getItem(key);
    },

    /**
     * Set item in storage (cookie for auth_token, localStorage for others)
     * @param {string} key - Storage key
     * @param {string} value - Value to store
     */
    setItem(key, value) {
        if (COOKIE_KEYS.includes(key)) {
            // Store auth token in cookie with 7-day expiration
            setCookie(key, value, { days: 7 });
        } else {
            localStorage.setItem(key, value);
        }
    },

    /**
     * Remove item from storage (cookie for auth_token, localStorage for others)
     * @param {string} key - Storage key
     */
    removeItem(key) {
        if (COOKIE_KEYS.includes(key)) {
            removeCookie(key);
        } else {
            localStorage.removeItem(key);
        }
    },

    /**
     * Clear all storage (both cookies and localStorage)
     * Note: This clears ALL localStorage, use with caution
     */
    clear() {
        // Remove all cookie keys
        COOKIE_KEYS.forEach(key => removeCookie(key));
        // Clear localStorage
        localStorage.clear();
    }
};

export default authStorage;
